import React, { useCallback, useEffect, useState } from 'react';
import TierPageBanner from '../components/portfolio/TierPageBanner';
import { Link } from 'react-router-dom';
import {
  Activity,
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  Database,
  FlaskConical,
  History,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { apiService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import { InterpretationPanel } from '../components/macro/MacroInterpretation';
import {
  MacroAiAllocationResponse,
  MacroAllocationResponse,
  MacroEconomicState,
  MacroMomentumDirection,
  MacroRegimeLabel,
  MacroStance,
  MacroSleeveCondition,
  MacroSleeveHeadline,
  MacroTargetSleeve,
  MacroTrackState,
} from '../types/macroData';

type WeightMode = 'rules' | 'ai';

interface CockpitSleeve {
  key: string;
  name: string;
  group: MacroTargetSleeve['group'];
  proxy: string;
  neutral: number;
  target: number;
  tilt: number;
  stance: MacroStance;
  detail: string;
  condition?: MacroSleeveCondition;
}

type Quadrant = Exclude<MacroRegimeLabel, 'mixed' | 'unknown'>;

const REGIME_NAMES: Record<MacroRegimeLabel, string> = {
  reflationary_expansion: 'Reflationary expansion',
  disinflationary_expansion: 'Disinflationary expansion',
  disinflationary_slowdown: 'Disinflationary slowdown',
  stagflationary: 'Stagflation',
  mixed: 'Mixed',
  unknown: 'Unknown',
};
const REGIME_PLAIN: Record<MacroRegimeLabel, string> = {
  reflationary_expansion: 'growth and inflation both running above expectations',
  disinflationary_expansion: 'growth above expectations with inflation below',
  disinflationary_slowdown: 'growth and inflation both running below expectations',
  stagflationary: 'growth below expectations with inflation above',
  mixed: 'one of growth or inflation is close to expectations',
  unknown: 'growth or inflation has no current reading',
};
const REGIME_COLORS: Record<MacroRegimeLabel, string> = {
  reflationary_expansion: '#f59e0b',
  disinflationary_expansion: '#16a34a',
  disinflationary_slowdown: '#0284c7',
  stagflationary: '#dc2626',
  mixed: '#9ca3af',
  unknown: '#e5e7eb',
};
const ODDS_ORDER: MacroRegimeLabel[] = [
  'disinflationary_expansion',
  'reflationary_expansion',
  'stagflationary',
  'disinflationary_slowdown',
  'mixed',
];
const GROUPS: Array<{ key: MacroTargetSleeve['group'][]; title: string }> = [
  { key: ['equity'], title: 'Equity sectors' },
  { key: ['bonds'], title: 'Bonds' },
  { key: ['real_assets', 'cash'], title: 'Real assets, dollar and cash' },
];
const INFLATION_NAMES: Record<string, string> = {
  core_pce_price_index: 'Core PCE',
  pce_price_index: 'PCE',
  cpi: 'CPI',
  core_cpi: 'Core CPI',
};
const STANCE_STYLES: Record<MacroTargetSleeve['stance'], string> = {
  overweight: 'bg-green-100 text-green-800',
  underweight: 'bg-red-100 text-red-800',
  neutral: 'bg-gray-100 text-gray-700',
};

const HEADLINE_LABEL: Record<MacroSleeveHeadline, string> = {
  extended_leader: 'Extended leader',
  extended_lagging: 'Extended laggard',
  healthy_leader: 'Healthy leader',
  healthy_laggard: 'Healthy laggard',
  recovering: 'Recovering',
  stressed: 'Stressed / fading',
  neutral: 'In line',
  'n/a': '—',
};

const HEADLINE_STYLE: Record<MacroSleeveHeadline, string> = {
  extended_leader: 'bg-orange-100 text-orange-900',
  extended_lagging: 'bg-amber-50 text-amber-900',
  healthy_leader: 'bg-green-50 text-green-900',
  healthy_laggard: 'bg-sky-50 text-sky-900',
  recovering: 'bg-emerald-100 text-emerald-900',
  stressed: 'bg-red-50 text-red-800',
  neutral: 'bg-gray-100 text-gray-700',
  'n/a': 'bg-gray-50 text-gray-500',
};

const signed = (v: number | null | undefined, digits = 1) =>
  v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(digits)}`;
const pct = (v: number) => `${Math.round(v * 100)}%`;
const shiftMonth = (label: string, months: number) => {
  const [y, m] = label.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + months, 1));
  return d.toISOString().slice(0, 7);
};

const MomentumArrow: React.FC<{ direction: MacroMomentumDirection }> = ({ direction }) =>
  direction === 'improving' ? (
    <ArrowUpRight className="h-4 w-4 text-blue-600" />
  ) : direction === 'deteriorating' ? (
    <ArrowDownRight className="h-4 w-4 text-orange-600" />
  ) : (
    <ArrowRight className="h-4 w-4 text-gray-400" />
  );

const Pill: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${className}`}>{children}</span>
);

/** Growth surprise (x) against inflation surprise (y), with the last year's path. */
const QuadrantMap: React.FC<{ path: Array<{ label: string; g: number; i: number }>; current: MacroRegimeLabel }> = ({
  path,
  current,
}) => {
  const size = 180;
  const span = Math.max(1.5, ...path.flatMap((p) => [Math.abs(p.g), Math.abs(p.i)])) * 1.15;
  const x = (g: number) => size / 2 + (g / span) * (size / 2);
  const y = (i: number) => size / 2 - (i / span) * (size / 2);
  const cells: Array<[Quadrant, number, number]> = [
    ['stagflationary', 0, 0],
    ['reflationary_expansion', size / 2, 0],
    ['disinflationary_slowdown', 0, size / 2],
    ['disinflationary_expansion', size / 2, size / 2],
  ];
  const last = path[path.length - 1];
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="h-44 w-44 flex-shrink-0">
      {cells.map(([label, cx, cy]) => (
        <rect
          key={label}
          x={cx}
          y={cy}
          width={size / 2}
          height={size / 2}
          fill={REGIME_COLORS[label]}
          fillOpacity={current === label ? 0.25 : 0.08}
        />
      ))}
      <line x1={size / 2} y1={0} x2={size / 2} y2={size} stroke="#9ca3af" strokeWidth={0.5} />
      <line x1={0} y1={size / 2} x2={size} y2={size / 2} stroke="#9ca3af" strokeWidth={0.5} />
      <text x={4} y={11} fontSize={8} fill="#991b1b">Stagflation</text>
      <text x={size - 4} y={11} fontSize={8} fill="#92400e" textAnchor="end">Reflation</text>
      <text x={4} y={size - 4} fontSize={8} fill="#075985">Slowdown</text>
      <text x={size - 4} y={size - 4} fontSize={8} fill="#166534" textAnchor="end">Goldilocks</text>
      <text x={size / 2 + 3} y={size / 2 - 3} fontSize={7} fill="#6b7280">growth →</text>
      <text x={size / 2 - 3} y={22} fontSize={7} fill="#6b7280" textAnchor="end">inflation ↑</text>
      {path.length > 1 && (
        <polyline
          points={path.map((p) => `${x(p.g)},${y(p.i)}`).join(' ')}
          fill="none"
          stroke="#4b5563"
          strokeWidth={1}
          strokeDasharray="2 2"
        />
      )}
      {path.slice(0, -1).map((p) => (
        <circle key={p.label} cx={x(p.g)} cy={y(p.i)} r={1.8} fill="#6b7280">
          <title>{p.label}</title>
        </circle>
      ))}
      {last && (
        <circle cx={x(last.g)} cy={y(last.i)} r={4.5} fill="#111827">
          <title>{`${last.label}: growth ${signed(last.g)} pp, inflation ${signed(last.i)} pp`}</title>
        </circle>
      )}
    </svg>
  );
};

const TrackLine: React.FC<{ title: string; track: MacroTrackState }> = ({ title, track }) => (
  <div className="flex items-center justify-between gap-2 text-sm">
    <span className="text-gray-600">{title}</span>
    <span className="flex items-center gap-1.5">
      <Pill
        className={
          track.position.growthState === 'expanding'
            ? 'bg-green-100 text-green-800'
            : track.position.growthState === 'contracting'
              ? 'bg-red-100 text-red-800'
              : 'bg-amber-100 text-amber-800'
        }
      >
        {track.position.growthState}
      </Pill>
      <span className="text-xs text-gray-500">{track.position.trendState.replace('_', ' ')}</span>
      <MomentumArrow direction={track.momentum.direction} />
      <span className="text-xs text-gray-700">
        {track.momentum.direction}
        {track.momentum.direction !== 'unknown' && !track.momentum.confirmed && ' (early)'}
      </span>
    </span>
  </div>
);

const Reading: React.FC<{ label: string; status: string; value?: string }> = ({ label, status, value }) => (
  <div className="flex items-baseline justify-between gap-2 text-sm">
    <span className="text-gray-600">{label}</span>
    <span className="text-right">
      <span className="font-medium text-gray-900">{status.replace(/_/g, ' ')}</span>
      {value && <span className="ml-1 text-xs text-gray-500">{value}</span>}
    </span>
  </div>
);

const OddsBar: React.FC<{ odds: Record<string, number>; label: string; note?: string }> = ({ odds, label, note }) => (
  <div>
    <div className="mb-1 flex justify-between text-xs text-gray-500">
      <span>{label}</span>
      {note && <span>{note}</span>}
    </div>
    <div className="flex h-5 overflow-hidden rounded">
      {ODDS_ORDER.map((l) => (
        <div
          key={l}
          style={{ width: `${(odds[l] ?? 0) * 100}%`, backgroundColor: REGIME_COLORS[l] }}
          className="flex items-center justify-center text-[10px] font-medium text-white"
          title={`${REGIME_NAMES[l]}: ${pct(odds[l] ?? 0)}`}
        >
          {(odds[l] ?? 0) >= 0.08 ? pct(odds[l]) : ''}
        </div>
      ))}
    </div>
  </div>
);

const RiskDial: React.FC<{ posture: MacroAllocationResponse['posture']; shift: number }> = ({ posture, shift }) => {
  const position = ((posture.score + 1) / 2) * 100;
  const tone = posture.score > 0.15 ? 'Risk-on' : posture.score < -0.15 ? 'Defensive' : 'Balanced';
  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-baseline justify-between">
          <span className="text-lg font-semibold text-gray-900">
            {tone} <span className="text-sm font-normal text-gray-500">({signed(posture.score, 2)})</span>
          </span>
          <span className="text-xs text-gray-500">{pct(posture.confidence)} confidence</span>
        </div>
        <div className="relative mt-2 h-2.5 rounded-full bg-gradient-to-r from-red-300 via-gray-200 to-green-300">
          <div
            className="absolute -top-1 w-1.5 rounded bg-gray-900"
            style={{ left: `calc(${position}% - 3px)`, height: '18px' }}
          />
        </div>
        <div className="mt-1 flex justify-between text-[10px] text-gray-400">
          <span>defensive</span>
          <span>risk-on</span>
        </div>
      </div>
      <ul className="space-y-1 text-xs">
        {posture.drivers
          .filter((d) => d.value !== null)
          .sort((a, b) => Math.abs(b.value! * b.weight) - Math.abs(a.value! * a.weight))
          .map((d) => (
            <li key={d.key} className="flex items-center justify-between gap-2">
              <span className="text-gray-700">{d.reason}</span>
              <span
                className={`font-mono ${d.value! > 0 ? 'text-green-700' : d.value! < 0 ? 'text-red-700' : 'text-gray-400'}`}
              >
                {signed(d.value, 1)}
              </span>
            </li>
          ))}
      </ul>
      <p className="text-[11px] text-gray-500">
        {shift === 0
          ? 'No weight moved by the dial.'
          : `Moves ${Math.abs(shift).toFixed(1)} points ${shift > 0 ? 'from Treasuries and cash into equities and credit' : 'from equities and credit into Treasuries and cash'}.`}
      </p>
    </div>
  );
};

const WeightBars: React.FC<{ sleeves: CockpitSleeve[] }> = ({ sleeves }) => {
  const max = Math.max(...sleeves.map((s) => Math.max(s.target, s.neutral)), 1);
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {GROUPS.map((g) => {
        const rows = sleeves.filter((s) => g.key.includes(s.group));
        const total = rows.reduce((a, s) => a + s.target, 0);
        const neutral = rows.reduce((a, s) => a + s.neutral, 0);
        return (
          <div key={g.title}>
            <div className="mb-2 flex items-baseline justify-between text-xs uppercase tracking-wide text-gray-500">
              <span>{g.title}</span>
              <span className="normal-case">
                {total.toFixed(1)}% <span className="text-gray-400">(neutral {neutral}%)</span>
              </span>
            </div>
            <div className="space-y-1.5">
              {rows.map((s) => (
                <div key={s.key} className="grid grid-cols-[7.5rem_1fr_3.5rem] items-center gap-2 text-xs">
                  <span className="truncate text-gray-700" title={s.name}>
                    {s.name}
                  </span>
                  <div className="relative h-3 rounded bg-gray-100">
                    <div
                      className={`absolute left-0 top-0 h-3 rounded ${s.tilt > 0.05 ? 'bg-green-500' : s.tilt < -0.05 ? 'bg-red-400' : 'bg-gray-400'}`}
                      style={{ width: `${(s.target / max) * 100}%` }}
                    />
                    <div
                      className="absolute -top-0.5 h-4 w-px bg-gray-900"
                      style={{ left: `${(s.neutral / max) * 100}%` }}
                      title={`Neutral ${s.neutral}%`}
                    />
                  </div>
                  <span className="text-right font-mono text-gray-900">
                    {s.target.toFixed(1)}
                    {Math.abs(s.tilt) >= 0.05 && (
                      <span className={s.tilt > 0 ? 'text-green-700' : 'text-red-700'}> {signed(s.tilt)}</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const sleeveReason = (s: MacroTargetSleeve, condition?: MacroSleeveCondition): string => {
  const parts: string[] = [];
  const d = s.drivers[0];
  if (d && Math.abs(d.contribution) > 0.005) {
    parts.push(
      `In ${REGIME_NAMES[d.label].toLowerCase()} (${pct(d.odds)} odds over 3-6 months) it has returned ${signed(d.edgeAnnualized)}% a year versus its own average (${d.months} months of history).`,
    );
  } else {
    parts.push('No regime gives it a meaningful historical edge right now.');
  }
  if (Math.abs(s.postureTilt) >= 0.05) {
    parts.push(`The risk dial ${s.postureTilt > 0 ? 'adds' : 'removes'} ${Math.abs(s.postureTilt).toFixed(1)} points.`);
  }
  if (Math.abs(s.rotationTilt ?? 0) >= 0.05) {
    const tag = condition ? HEADLINE_LABEL[condition.headline].toLowerCase() : 'sector condition';
    parts.push(
      `Sector rotation ${s.rotationTilt! > 0 ? 'adds' : 'removes'} ${Math.abs(s.rotationTilt!).toFixed(1)} points (${tag}).`,
    );
  }
  return parts.join(' ');
};

const ConditionGauge: React.FC<{ c: MacroSleeveCondition; name: string; rotationTilt?: number }> = ({
  c,
  name,
  rotationTilt,
}) => (
  <div className="rounded-lg border border-gray-200 bg-white p-3">
    <div className="flex items-start justify-between gap-2">
      <div className="text-sm font-semibold text-gray-900">{name}</div>
      <Pill className={HEADLINE_STYLE[c.headline]}>{HEADLINE_LABEL[c.headline]}</Pill>
    </div>
    <dl className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-[11px] text-gray-600">
      <div>
        <dt className="text-gray-400">12m return z</dt>
        <dd className="font-mono text-gray-900">{c.return12mZ !== null ? signed(c.return12mZ, 2) : '—'}</dd>
      </div>
      <div>
        <dt className="text-gray-400">Vs peer sectors</dt>
        <dd className="font-mono text-gray-900">
          {c.peerRelative12mPp !== null ? `${signed(c.peerRelative12mPp, 1)} pp` : '—'}
        </dd>
      </div>
      <div>
        <dt className="text-gray-400">Extension</dt>
        <dd className="capitalize text-gray-900">{c.extension}</dd>
      </div>
      <div>
        <dt className="text-gray-400">Leadership</dt>
        <dd className="capitalize text-gray-900">
          {c.leadership === 'unknown' ? '—' : c.leadership.replace('_', ' ')}
        </dd>
      </div>
      <div>
        <dt className="text-gray-400">RS trend (3m)</dt>
        <dd className="capitalize text-gray-900">{c.trend}</dd>
      </div>
      <div>
        <dt className="text-gray-400">Rules rotation</dt>
        <dd className="font-mono text-gray-900">
          {rotationTilt !== undefined && Math.abs(rotationTilt) >= 0.05 ? signed(rotationTilt, 1) : '—'}
        </dd>
      </div>
    </dl>
  </div>
);

const SleeveCard: React.FC<{ sleeve: CockpitSleeve }> = ({ sleeve: s }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-3">
    <div className="flex items-start justify-between gap-2">
      <div className="text-sm font-semibold text-gray-900">{s.name}</div>
      <Pill className={STANCE_STYLES[s.stance]}>{s.stance}</Pill>
    </div>
    <div className="mt-1 text-2xl font-semibold text-gray-900">
      {s.target.toFixed(1)}%
      <span className="ml-2 text-xs font-normal text-gray-500">
        neutral {s.neutral}% · {signed(s.tilt)} pts
      </span>
    </div>
    <p className="mt-2 text-xs text-gray-700">{s.detail}</p>
    <p className="mt-2 text-[11px] text-gray-400">Measured with: {s.proxy}</p>
  </div>
);

const MacroCockpit: React.FC = () => {
  const [asOf, setAsOf] = useState<string | null>(null);
  const [weightMode, setWeightMode] = useState<WeightMode>('rules');
  const [allocation, setAllocation] = useState<MacroAllocationResponse | null>(null);
  const [aiWeights, setAiWeights] = useState<MacroAiAllocationResponse | null>(null);
  const [state, setState] = useState<MacroEconomicState | null>(null);
  const [path, setPath] = useState<Array<{ label: string; g: number; i: number }>>([]);
  const [monthsInRegime, setMonthsInRegime] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generatingAi, setGeneratingAi] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [alloc, evidence, ai] = await Promise.all([
        apiService.getMacroAllocation(asOf ?? undefined),
        apiService.getMacroStateEvidence({ mode: 'latest_revised', asOf: asOf ?? undefined }),
        apiService.getMacroAiAllocation(asOf ?? undefined),
      ]);
      setAiWeights(ai);
      setAllocation(alloc);
      setState(evidence.state);
      const label = alloc.asOfLabel;
      const history = await apiService.getMacroStateHistory({
        mode: 'latest_revised',
        from: `${shiftMonth(label, -24)}-01`,
        to: `${shiftMonth(label, 1)}-01`,
      });
      const months = history.states.filter((s) => s.asOfLabel <= label);
      setPath(
        months
          .slice(-12)
          .filter((s) => s.regime?.growth.score !== null && s.regime?.inflation.score !== null && s.regime)
          .map((s) => ({ label: s.asOfLabel, g: s.regime!.growth.score!, i: s.regime!.inflation.score! })),
      );
      let run = 0;
      for (let k = months.length - 1; k >= 0 && months[k].regime?.label === alloc.regime; k--) run += 1;
      setMonthsInRegime(run);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load the cockpit');
      setAllocation(null);
    } finally {
      setLoading(false);
    }
  }, [asOf]);

  useEffect(() => {
    load();
  }, [load]);

  const generateAi = async () => {
    if (!allocation) return;
    setGeneratingAi(true);
    setError(null);
    try {
      const outcome = await apiService.generateMacroAiAllocation(allocation.asOfLabel);
      if (outcome.status === 'busy' || outcome.status === 'no_state') setError(outcome.reason);
      else if (outcome.status === 'failed') setError(`AI weights could not be generated: ${outcome.error}`);
      else if (outcome.status === 'rejected') {
        const detail = outcome.weights.validation?.errors?.slice(0, 2).join('; ');
        setError(detail ? `Validation failed: ${detail}` : 'The model response failed validation; try again later.');
      }
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'AI weight generation failed');
    } finally {
      setGeneratingAi(false);
    }
  };

  const fw = state?.frameworks;
  const regime = state?.regime;
  const aiRecord = aiWeights?.weights?.status === 'accepted' ? aiWeights.weights : null;

  const conditionByKey = new Map(allocation?.sleeveConditions?.map((c) => [c.sleeveKey, c]) ?? []);
  const ruleSleeves: CockpitSleeve[] =
    allocation?.sleeves.map((s) => ({
      ...s,
      condition: conditionByKey.get(s.key),
      detail: sleeveReason(s, conditionByKey.get(s.key)),
    })) ?? [];
  const aiSleeves: CockpitSleeve[] =
    aiRecord?.sleeves.map((s) => ({ ...s, detail: s.rationale })) ?? [];
  const displaySleeves = weightMode === 'rules' ? ruleSleeves : aiSleeves;
  const totalShift =
    weightMode === 'rules' ? allocation?.totalShift ?? 0 : aiRecord?.totalShift ?? 0;
  const movers = [...displaySleeves].filter((s) => s.stance !== 'neutral').sort((a, b) => b.tilt - a.tilt);

  return (
    <div className="space-y-6">
      <TierPageBanner
        title="Macro · Tier 1 consumer"
        description="Cycle, regime, and sleeve weights feed the Tier 1 macro ETF portfolio module."
        primaryTier={1}
        thesisTimeframe="6_months_plus"
      />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marlin Macro Cockpit</h1>
          <p className="text-sm text-gray-500">
            Where the cycle is, which regime is likely next, and the capital weights the rules derive from it.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1 text-sm text-gray-600">
            <History className="h-4 w-4" /> As of
            <input
              type="month"
              value={allocation?.asOfLabel ?? ''}
              onChange={(e) => e.target.value && setAsOf(e.target.value)}
              className="rounded border-gray-300 py-1 text-sm"
            />
          </label>
          {asOf && (
            <button
              onClick={() => setAsOf(null)}
              className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm hover:bg-gray-50"
            >
              Latest
            </button>
          )}
          <button
            onClick={load}
            className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" /> Reload
          </button>
          <Link
            to="/app/macro/cycle"
            className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            <Activity className="h-4 w-4" /> Cycle detail
          </Link>
          <Link
            to="/app/macro/data"
            className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            <Database className="h-4 w-4" /> Data
          </Link>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        <FlaskConical className="h-5 w-5 flex-shrink-0" />
        <span>
          Research only. Weights are unvalidated and nothing trades on them yet. Toggle Rules for the fixed engine
          (regime evidence plus risk dial) or AI for an alternative view from the same readings. Past months replay
          with only the data available then.
        </span>
      </div>

      {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {loading && !allocation ? (
        <LoadingSpinner />
      ) : !allocation || !state ? (
        <Card>
          <CardContent>
            <p className="py-6 text-center text-sm text-gray-500">
              No target weights yet. They appear after asset returns load on the next weekly backfill.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className={loading ? 'space-y-6 opacity-60' : 'space-y-6'}>
          <Card>
            <CardContent>
              {weightMode === 'ai' && aiRecord ? (
                <div className="space-y-2 py-1 text-base text-gray-900">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-purple-800">
                      <Sparkles className="h-4 w-4" /> AI positioning · end of {allocation.asOfLabel}
                    </span>
                    {!aiWeights?.current && (
                      <span className="text-xs text-amber-700">Written before the latest readings; regenerate to refresh.</span>
                    )}
                  </div>
                  <p>{aiRecord.summary}</p>
                  {aiRecord.risks && <p className="text-sm text-gray-600">{aiRecord.risks}</p>}
                </div>
              ) : (
                <div className="py-1 text-base text-gray-900">
                  <span className="font-semibold">End of {allocation.asOfLabel}{allocation.provisional && ' (month in progress)'}:</span>{' '}
                  {REGIME_NAMES[allocation.regime].toLowerCase()} regime ({REGIME_PLAIN[allocation.regime]}), risk dial{' '}
                  {signed(allocation.posture.score, 2)}.{' '}
                  {movers.length ? (
                    <>
                      Largest tilts:{' '}
                      {[...movers.slice(0, 3), ...movers.slice(-3).filter((m) => !movers.slice(0, 3).includes(m))]
                        .map((s) => `${s.name} ${signed(s.tilt)}`)
                        .join(', ')}
                      . {totalShift.toFixed(1)} points moved from neutral.
                    </>
                  ) : (
                    'Weights stay at neutral.'
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            <Card>
              <CardHeader>
                <CardTitle>Dalio regime</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center gap-2">
                  <QuadrantMap path={path} current={allocation.regime} />
                  <div className="text-center">
                    <div className="text-sm font-semibold" style={{ color: REGIME_COLORS[allocation.regime] === '#9ca3af' ? '#374151' : REGIME_COLORS[allocation.regime] }}>
                      {REGIME_NAMES[allocation.regime]}
                    </div>
                    <div className="text-xs text-gray-500">
                      {monthsInRegime} month{monthsInRegime === 1 ? '' : 's'} in this regime · dots show the last 12 months
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Debt cycle</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <TrackLine title="Real economy" track={state.real} />
                <TrackLine title="Credit" track={state.credit} />
                <div className="border-t border-gray-100 pt-2 text-xs text-gray-600">
                  {state.divergence.momentumOpposed
                    ? state.credit.momentum.direction === 'deteriorating'
                      ? 'Warning: credit is rolling over while the real economy still improves.'
                      : 'Credit is improving ahead of the real economy.'
                    : 'Credit and the real economy are not diverging.'}
                  {state.divergence.value !== null &&
                    ` Credit sits ${Math.abs(state.divergence.value).toFixed(2)} standard deviations ${state.divergence.value >= 0 ? 'above' : 'below'} real activity.`}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Inflation and policy</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Reading
                  label="Growth surprise"
                  status={regime?.growth.direction === 'upside' ? 'above expected' : regime?.growth.direction === 'downside' ? 'below expected' : regime?.growth.direction ?? 'unknown'}
                  value={regime?.growth.score !== null && regime?.growth.score !== undefined ? `${signed(regime.growth.score)} pp` : undefined}
                />
                <Reading
                  label="Inflation surprise"
                  status={regime?.inflation.direction === 'upside' ? 'above expected' : regime?.inflation.direction === 'downside' ? 'below expected' : regime?.inflation.direction ?? 'unknown'}
                  value={regime?.inflation.score !== null && regime?.inflation.score !== undefined ? `${signed(regime.inflation.score)} pp` : undefined}
                />
                {regime?.inflationContext?.slice(0, 2).map((c) => (
                  <Reading
                    key={c.seriesKey}
                    label={`${INFLATION_NAMES[c.seriesKey] ?? c.seriesKey} inflation, y/y`}
                    status={`${c.value.toFixed(1)}%`}
                    value={c.periodLabel}
                  />
                ))}
                <Reading
                  label="Real policy rate"
                  status={fw?.classical.realRates.status ?? 'unknown'}
                  value={fw?.classical.realRates.value !== null && fw?.classical.realRates.value !== undefined ? `${signed(fw.classical.realRates.value)} pp` : undefined}
                />
                <Reading
                  label="Yield curve"
                  status={fw?.curve.status ?? 'unknown'}
                  value={fw?.curve.value !== null && fw?.curve.value !== undefined ? `${signed(fw.curve.value)} pp` : undefined}
                />
                <Reading
                  label="Labour market"
                  status={fw?.classical.labor.status ?? 'unknown'}
                  value={fw ? `unemployment ${fw.classical.labor.trend}` : undefined}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Dalio vs classical</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Reading label="Dalio quadrant" status={REGIME_NAMES[allocation.regime].toLowerCase()} />
                <Reading label="Money vs prices (Fisher)" status={fw?.classical.quantityTheory.status ?? 'unknown'} />
                <Reading label="Wages vs productivity (Ricardo)" status={fw?.classical.realWages.status ?? 'unknown'} />
                <Reading label="Real interest rate" status={fw?.classical.realRates.status ?? 'unknown'} />
                <p className="border-t border-gray-100 pt-2 text-xs text-gray-600">
                  Dalio reads growth and inflation as surprises against expectations; the classical lens asks whether
                  money, wages and interest rates are in balance. The interpretation below explains where they agree.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Regime odds</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {allocation.outlook.map((o) => (
                  <OddsBar
                    key={o.horizon}
                    odds={o.odds}
                    label={`${o.horizon} months ahead`}
                    note={o.unconditional ? 'long-run frequencies' : `${o.sampleMonths} past months started here`}
                  />
                ))}
                <div className="flex flex-wrap gap-3 pt-1 text-[11px] text-gray-600">
                  {ODDS_ORDER.map((l) => (
                    <span key={l} className="inline-flex items-center gap-1">
                      <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: REGIME_COLORS[l] }} />
                      {REGIME_NAMES[l]}
                    </span>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400">
                  How often each regime followed a month in today's regime, counted over history up to this month. Not
                  a forecast model; the 3- and 6-month odds drive the weights.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Risk dial</CardTitle>
              </CardHeader>
              <CardContent>
                <RiskDial posture={allocation.posture} shift={allocation.postureShift} />
              </CardContent>
            </Card>
          </div>

          {(allocation.sleeveConditions?.length > 0 || allocation.evidenceThrough) && (
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <CardTitle>Sector condition</CardTitle>
                  <span className="text-xs text-gray-500">
                    {allocation.sleeveConditionVersion} · equal-weight equity peers · returns through{' '}
                    {allocation.conditionThrough ?? allocation.sleeveConditions[0]?.asOfMonth ?? '—'}
                    {allocation.conditionThrough &&
                      allocation.conditionThrough !== allocation.asOfLabel &&
                      ` (cycle state ${allocation.asOfLabel})`}
                    {Math.abs(allocation.rotationShift) > 0.05 &&
                      weightMode === 'rules' &&
                      ` · rotation overlay ${Math.abs(allocation.rotationShift).toFixed(1)} pts among sectors`}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="mb-3 text-xs text-gray-600">
                  Z-scores use each sleeve&apos;s own 12-month return history. Peer strength is versus the other equity
                  sleeves we allocate to (not the broad market). Gauges use the latest month all equity sleeves share
                  (French industry returns often trail FRED bond months shown under target weights). Extended leaders
                  tend to be trimmed slightly; recovering laggards tend to be added—on top of regime evidence and the
                  risk dial.
                </p>
                {!allocation.evidenceThrough ? (
                  <p className="text-sm text-amber-800">
                    No asset return history in the database yet. Sector gauges use the same French/FRED sleeve
                    returns as target weights—load them once with{' '}
                    <code className="rounded bg-amber-100 px-1">npm run macro:assets</code> locally, or trigger{' '}
                    <code className="rounded bg-amber-100 px-1">POST /macro/cron/ingest</code> (or the weekly macro
                    backfill) in production, then reload this page.
                  </p>
                ) : !allocation.conditionThrough || allocation.sleeveConditions.length === 0 ? (
                  <p className="text-sm text-amber-800">
                    Returns are loaded (through {allocation.evidenceThrough}), but no month was found where every equity
                    sleeve has a full 12-month window through the cycle as-of date. Reload after the next asset refresh,
                    or pick an earlier month in the as-of picker.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {allocation.sleeveConditions
                      .filter((c) => c.leadership !== 'n/a')
                      .map((c) => {
                        const sleeve = allocation.sleeves.find((s) => s.key === c.sleeveKey);
                        return (
                          <ConditionGauge
                            key={c.sleeveKey}
                            c={c}
                            name={sleeve?.name ?? c.sleeveKey}
                            rotationTilt={sleeve?.rotationTilt}
                          />
                        );
                      })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-3">
                  <CardTitle>Target capital weights</CardTitle>
                  <div className="inline-flex rounded-md border border-gray-300 p-0.5 text-sm">
                    {(
                      [
                        ['rules', 'Rules'],
                        ['ai', 'AI'],
                      ] as const
                    ).map(([mode, label]) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setWeightMode(mode)}
                        className={`rounded px-2.5 py-0.5 ${
                          weightMode === mode ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <span className="text-xs text-gray-500">
                  {totalShift.toFixed(1)} of 25 points moved · ±5 per sleeve
                  {weightMode === 'rules' && (
                    <>
                      {' '}
                      · rules {allocation.allocationVersion}
                      {allocation.evidenceThrough && ` · returns through ${allocation.evidenceThrough}`}
                    </>
                  )}
                  {weightMode === 'ai' && aiRecord?.model && ` · ${aiRecord.model}`}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              {weightMode === 'ai' && !aiRecord ? (
                <div className="space-y-3 py-4 text-center text-sm text-gray-600">
                  <p>No AI weights for this month yet. They are generated after each evening brief, or on demand here.</p>
                  <button
                    type="button"
                    onClick={generateAi}
                    disabled={generatingAi}
                    className="inline-flex items-center gap-2 rounded-md border border-purple-300 bg-purple-50 px-3 py-1.5 text-sm text-purple-800 hover:bg-purple-100 disabled:opacity-50"
                  >
                    <RefreshCw className={`h-4 w-4 ${generatingAi ? 'animate-spin' : ''}`} />
                    {generatingAi ? 'Generating (up to a minute)…' : 'Generate AI weights'}
                  </button>
                </div>
              ) : (
                <>
                  <WeightBars sleeves={displaySleeves} />
                  <p className="mt-3 text-[11px] text-gray-400">
                    Bar: target weight (green above neutral, red below); tick: neutral weight. Neutral is 55% equity
                    sectors, 30% bonds, 15% gold, real estate and cash.
                    {weightMode === 'ai' && ' AI weights use the same caps as the rules but are not the trading hand-off.'}
                  </p>
                  {weightMode === 'ai' && aiRecord && !aiWeights?.current && (
                    <button
                      type="button"
                      onClick={generateAi}
                      disabled={generatingAi}
                      className="mt-3 inline-flex items-center gap-2 rounded-md border border-purple-300 bg-purple-50 px-3 py-1.5 text-sm text-purple-800 hover:bg-purple-100 disabled:opacity-50"
                    >
                      <RefreshCw className={`h-4 w-4 ${generatingAi ? 'animate-spin' : ''}`} />
                      Regenerate AI weights
                    </button>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {(weightMode === 'rules' || aiRecord) &&
            GROUPS.map((g) => (
              <div key={g.title}>
                <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">{g.title}</h2>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {displaySleeves
                    .filter((s) => g.key.includes(s.group))
                    .map((s) => (
                      <SleeveCard key={s.key} sleeve={s} />
                    ))}
                </div>
              </div>
            ))}

          <InterpretationPanel mode="latest_revised" asOfLabel={allocation.asOfLabel} />
        </div>
      )}
    </div>
  );
};

export default MacroCockpit;
